/**
 * A list of short entries — patient populations, services provided — typed
 * one at a time.
 *
 * Type, press Enter (or a comma), and the entry becomes a chip with its own
 * Remove button. Pasting "Adults, Pediatrics, Neonates" adds three. Both
 * limits are visible: the chip count against the cap, and the character
 * count on the box. A screen reader hears the count change and can reach
 * every Remove.
 *
 * Deliberately not a free-text textarea split on newlines: the backend
 * stores an array with a cap on entries and a cap per entry, and a person
 * should see those edges while typing rather than on save.
 */

import { useId, useState, type KeyboardEvent, type ClipboardEvent } from 'react';
import { CharCount } from './CharCount';
import form from './Form.module.css';
import styles from './ListInput.module.css';

export interface ListInputProps {
  id: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** Most entries the list may hold. */
  max: number;
  /** Most characters one entry may have. */
  itemMax: number;
  placeholder?: string;
  disabled?: boolean;
  /** The id of the visible label; the input's accessible name. */
  labelledBy: string;
  describedBy?: string;
}

/** Split a paste or a typed line into entries: trimmed, non-empty, distinct. */
export function splitEntries(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[,\n]/)) {
    const item = raw.trim();
    if (item && !out.includes(item)) out.push(item);
  }
  return out;
}

export function ListInput({
  id, value, onChange, max, itemMax, placeholder, disabled, labelledBy, describedBy,
}: ListInputProps) {
  const [draft, setDraft] = useState('');
  const countId = useId();
  const full = value.length >= max;

  function add(text: string) {
    const fresh = splitEntries(text)
      .map((s) => s.slice(0, itemMax))
      .filter((s) => !value.includes(s));
    if (!fresh.length) return;
    onChange([...value, ...fresh].slice(0, max));
    setDraft('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length) {
      // The way every tag box works: backspace on an empty box removes the
      // last chip. Not destructive — it was one keystroke to add.
      onChange(value.slice(0, -1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (/[,\n]/.test(text)) {
      e.preventDefault();
      add(draft + text);
    }
  }

  return (
    <div className={styles.wrap}>
      {value.length > 0 && (
        <ul className={styles.chips} aria-labelledby={labelledBy}>
          {value.map((item) => (
            <li key={item} className={styles.chip}>
              <span>{item}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => onChange(value.filter((v) => v !== item))}
                  aria-label={`Remove ${item}`}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.entry}>
        <input
          id={id}
          className={form.input}
          type="text"
          value={draft}
          maxLength={itemMax}
          disabled={disabled || full}
          placeholder={full ? `Up to ${max} entries` : (placeholder ?? 'Type and press Enter to add')}
          onChange={(e) => setDraft(e.target.value.slice(0, itemMax))}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => { if (draft.trim()) add(draft); }}
          aria-labelledby={labelledBy}
          aria-describedby={[describedBy, countId].filter(Boolean).join(' ')}
          autoComplete="off"
        />
        <button
          type="button"
          className={styles.add}
          onClick={() => add(draft)}
          disabled={disabled || full || !draft.trim()}
        >
          Add
        </button>
      </div>
      <p className={styles.count} id={countId} aria-live="polite">
        <span className="tnum">{value.length}</span> of {max}
        {draft && <CharCount value={draft} max={itemMax} />}
      </p>
    </div>
  );
}
